import { all, create } from "mathjs";
import type { ExchangeRateData } from "./types.js";

const MEAN_GREGORIAN_YEAR_SECONDS = 31_556_952;

export type MathInstance = ReturnType<typeof create>;

type Props = {
    emCss: number;
};

export function createMathInstance(props: Props): MathInstance {
    const math = create(all!);
    math.createUnit("year", `${MEAN_GREGORIAN_YEAR_SECONDS} s`, { override: true });
    math.createUnit("years", `${MEAN_GREGORIAN_YEAR_SECONDS} s`, { override: true });
    math.createUnit("px");
    math.createUnit("pt", "1.3333 px", { override: true });
    math.createUnit("em", `${props.emCss || 16} px`, { override: true });
    return math;
}

export function reconfigureEm(math: MathInstance, emPx: number): MathInstance {
    try {
        math.createUnit("em", `${emPx} px`, { override: true });
    } catch (error) {
        console.warn("Failed to reconfigure math em unit", error);
    }
    return math;
}

function registerCurrencyUnits(math: MathInstance, ratesData: ExchangeRateData): void {
    try {
        math.createUnit("EUR", { aliases: ["eur", "euro"] });
        Object.entries(ratesData.rates).forEach(([code, rate]) => {
            if (code !== "EUR") {
                try {
                    math.createUnit(code, math.unit(1 / rate, "EUR"));
                } catch (error) {
                    console.warn(`Failed to register currency unit ${code}`, error);
                }
            }
        });
    } catch (error) {
        console.warn("Failed to register base currency units", error);
    }
}

let cached: MathInstance | null = null;
let cachedTs: number | null = null;

export function getMathInstance(emCss: number, ratesData: ExchangeRateData | null): MathInstance {
    const ts = ratesData?.timestamp ?? null;
    if (cached === null || cachedTs !== ts) {
        cached = createMathInstance({ emCss });
        cachedTs = ts;
        if (ratesData) registerCurrencyUnits(cached, ratesData);
        return cached;
    }
    reconfigureEm(cached, emCss);
    return cached;
}

export function __resetMathInstanceCache(): void {
    cached = null;
    cachedTs = null;
}
